//  ----------      START   IMPORT  BLOCK       ----------
import { useEffect, useRef, useState, useMemo } from 'react';
//  Hooks
import useDebouncedFetch from '../../../../API/Hooks/useDebouncedFetch';
//  Widgets
import ListBoxLocal from '../../../SearchBox/listBoxLocal';
import Quill from 'quill';
//  Config
import prescriptionVocabulary from '../../../../ConfigFiles/prescription_vocabulary';
import 'quill/dist/quill.snow.css';
//  ----------      END   IMPORT  BLOCK       ----------


const PrescriptionTextBox = ({placeholder, debounceWait, listBox, noItemMessage, errorMessage, promise, onTextChange}) => {
  const quillRef = useRef(null);

  const {instructionVerbs, drugsUnits, periodicityUnits, durationUnits, conjunctions, prepositions, indicationOfUse, administrationRoutes } = prescriptionVocabulary;
  
  //  For processing an localizing active words
  const cursorPosition = useRef(null);

  const [lineNumber, setLineNumber] = useState(null);
  const [relativePosition, setRelativePosition] = useState(null);

  const [activeIndex, setActiveIndex] = useState(null);
  const activeIndexRef = useRef(activeIndex);

  const [localactiveIndex, setLocalactiveIndex] = useState(null)
  const localactiveIndexRef = useRef(localactiveIndex);

  const [part_active, set_part_active] = useState(0)
  const partActiveRef = useRef(part_active);

  const [current_word, set_current_word] = useState(null)
  const current_wordRef = useRef(current_word)

  const [query, setQuery] = useState("");
  const [data, setData, error2] = useDebouncedFetch(query, promise, debounceWait);
  const dataRef = useRef(data);
  const [queryTarget, setQueryTarget] = useState(['', 0])
  
  //  Use useMemo to cache the result based on queryTarget
  const localData = useMemo(() => fetchLocalVocabulary(queryTarget), [queryTarget]);
  //  var [localData] = [fetchLocalVocabulary(queryTarget)]
  //  Ref for access in keyboard handlers
  const localDataRef = useRef(localData);
  


  //  Sync Effects, essential for Quill's keyboard handlers to work.
  useEffect(() => {
    current_wordRef.current = current_word;
    localDataRef.current = localData;
    partActiveRef.current = part_active;
    dataRef.current = data;
    localactiveIndexRef.current = localactiveIndex;
    activeIndexRef.current = activeIndex;
  }, [current_word, localData, part_active, data, localactiveIndex, activeIndex]);
  //  END: Sync Effects

  const handleArrowDown = (range, context) => {
    const event = context.event;
    //  Continue as normal if there's no active word on display/selection.
    if (partActiveRef.current < 1) return true;
    //  Prevent movements inside the text, so the movements take place inside the suggestions list.
    event.preventDefault();
    //  Changes active index of API list.
    if (partActiveRef.current === 1 && dataRef.current.length > 0) {
      setActiveIndex((prevIndex) => (prevIndex === null || prevIndex === dataRef.current.length - 1 ? 0 : prevIndex + 1));
    }
    //  Changes active index of local list.
    if (partActiveRef.current > 1 && localDataRef.current.length > 0){
      setLocalactiveIndex((prevIndex) => {
        if (prevIndex === null || prevIndex >= localDataRef.current.length - 1) {
          //  Reset if out of bounds or at the end.
          return 0; 
        } else {
          //  Increment if within bounds
          return prevIndex + 1;
        }
      });
    }
    //  Expected bool as return.
    return false;
  };

  const handleArrowUp = (range, context) => {
    const event = context.event;
    if (partActiveRef.current < 1) return true;
    //  Prevents movements inside the text, movements take place inside the suggestions list.
    event.preventDefault();
    //  Changes active index of API list.
    if (partActiveRef.current === 1 && dataRef.current.length > 0) {
      setActiveIndex((prevIndex) => (prevIndex === 0 ? dataRef.current.length - 1 : prevIndex - 1));
    }
    //  Changes active index of local list.
    if (partActiveRef.current > 1 && localDataRef.current.length > 0) {
      setLocalactiveIndex((prevIndex) => {
        return prevIndex === 0 ? localDataRef.current.length - 1 : prevIndex - 1;
      });
    }
    //  Expected bool as return.
    return false;
  };

  const handleItemClick = (index) => {
    setActiveIndex(index);
    // Refocus on the editor after a short delay
    setTimeout(() => {
      if (document.activeElement !== quillRef.current.root) {
        quillRef.current.focus();
      }
    }, 100); // Adjust delay as necessary
  };

  const handleEnterKey = (range, context) => {
    //  Check if there are active sugestions.
    if (partActiveRef.current < 1 || (activeIndexRef?.current == null && localactiveIndexRef?.current == null)) {
      //  Important! This allows the default behavior to proceed (break line).
      return true; 
    }
    //  To mutate depending on scenario.
    let text_to_insert = '';
    //  Vocabulary is drurug related retrieved from API.
    if (partActiveRef.current === 1 && activeIndexRef?.current != null){
      //  Extract text.
      text_to_insert = dataRef.current[activeIndexRef.current].name;
      //  Prevent infinite loop of adding drug.
      set_part_active(0)
      //  Clear suggestions list.
      setQuery('')
      //  Remove active index.
      setActiveIndex(null)
    }
    //  Vocabulary is retrieved from local data.
    if (partActiveRef.current > 1 && localactiveIndexRef?.current != null) {
      //  Extract text.
      text_to_insert = localDataRef.current[localactiveIndexRef.current];
    }

    //  No text to append.
    if (!text_to_insert){
      //  Proceed with default behavior.
      return true;
    }
    //  Calculate position to replace the active word. 
    const active_word_start = cursorPosition.current - current_wordRef.current.length;
    const active_word_end = cursorPosition.current;
    //  Retrieve text to be modified.
    const currentText = quillRef.current.getText();
    //  Insert selected text.
    //  Text before string to insert.
    const beforeWord = currentText.slice(0, active_word_start);
    //  Text after text to insert.
    const afterWord = currentText.slice(cursorPosition.current);
    //  New and composed text.
    const newText = beforeWord + text_to_insert + ' ' + afterWord;
          
    //  Update Quills contents.
    quillRef.current.setText(newText);

    //  Update cursor position for smooth interaction:
    //  Move right lenght of the inserted text.
    //  +1 right becuase of the space.
    const target_position = active_word_start + text_to_insert.length + 1
    //  Set text.
    quillRef.current.setSelection(target_position);
    //  Important! This allows the default behavior to proceed (break line).
    return false;
  };
  const bindings = {
    ENTER: {
      key: "Enter",
      handler: handleEnterKey
    }
  }

  function fetchLocalVocabulary (queryTarget) {
    switch (queryTarget[1]) {
      case 2:
        return instructionVerbs.filter(item => item.toLowerCase().includes(queryTarget[0].trim().toLowerCase()));
      case 3:
        return drugsUnits.filter(item => item.toLowerCase().includes(queryTarget[0].trim().toLowerCase()));
      case 4:
        return periodicityUnits.filter(item => item.toLowerCase().includes(queryTarget[0].trim().toLowerCase()));
      case 5:
        return administrationRoutes.filter(item => item.toLowerCase().includes(queryTarget[0].trim().toLowerCase()));
      case 6:
        return durationUnits.filter(item => item.toLowerCase().includes(queryTarget[0].trim().toLowerCase()));
      case 7:
        return conjunctions.filter(item => item.toLowerCase().includes(queryTarget[0].trim().toLowerCase()));
      case 8:
        return prepositions.filter(item => item.toLowerCase().includes(queryTarget[0].trim().toLowerCase()));
      case 9:
        return indicationOfUse.filter(item => item.toLowerCase().includes(queryTarget[0].trim().toLowerCase()));
      default:
          return []
  }
  }
  //  Helper functions
  const identifyInstructionsBlocks = (full_text) => {
    //  Keep track of delimiter positions
    const delimiters_positions = []
    //  Regex to match digit parenthesis
    const number_parentheses_regex = /^\d{1,2}\)$/;
    //  Break text into lines
    const lines = full_text.split('\n')
    //  Instruction must conformed by at least 2 lines.
    if (lines.length < 1) {
      return delimiters_positions;
    }
    //  Search for delimiter
    lines.forEach((line) => {
      //  Pass only lines conforming with regex
      if (number_parentheses_regex.test(line)){
        //  Retrieve number duplicates.
        const position = full_text.indexOf(line)
        if (position.length > 1){
          console.log(`instruction of index ${line} is being repeated.`)
        }
        //  Retrieve instruction number only.
        const instruction_number = line.split(')')[0]
        //  Save instruction with it's position.
        delimiters_positions.push([parseInt(instruction_number), position])
      } 
    });
    //  Make positions accesible.
    return delimiters_positions;
  }

  const identifyActiveInstruction = (delimiters, cursor_position) =>{
    //  No instructions.
    if (delimiters < 0) {return [];}
    //  One instruction.
    if (delimiters.length === 1 ){
      //  Access sole position.
      if (delimiters[0][1] + 1 < cursor_position){
        //  Return sole instruction.
        return delimiters[0];
      }
      else {
        //  Redacting before first instruction
        return []
      }
    } 
    else {
      //  Keep track of active instruction.
      let active_instruction = []
      delimiters.some((instruction_position, index) => {
        //  Access position
        if (instruction_position[1] + 1 < cursor_position){
          active_instruction = delimiters[index]
        } else{
          //  Redacting before first instruction
          return active_instruction;
        }
      });
      return active_instruction;
    }
  }

  const separate_instructions = (full_text, delimiters) => {
    //  No instructions.
    if (delimiters < 0) {return [];}
    //  One instruction.
    if (delimiters.length === 1 ){
        //  Ignore number, parethesis & line break will result in extracted insturction.
        return [full_text.slice(delimiters[0][1] + delimiters[0][0].toString().length + 2).trim()];
      }
    //  1+ instructions.
    let extracted_instructions = []
    delimiters.forEach((instruction, index) => {

      //  Instruction isn't the last.
      if (index + 1 !== delimiters.length){

          const next_instruction_start = delimiters[index + 1][1]
          //  Ignore number, parenthesis and linebreak, and add that to array extracted instruction.
           extracted_instructions.push(full_text.slice(instruction[1] + delimiters[0][0].toString().length + 2, next_instruction_start).trim())
          //  Skip to next iteration.
          return;
        }
        //  Ignore number, parenthesis and linebreak and add that to array extracted instruction.
        extracted_instructions.push(full_text.slice(+ delimiters[0][0].toString().length + 2 + instruction[1]).trim())
    });
        //  Return extracted instructions.
        return extracted_instructions;
  }

  function fraction_to_float (numerator, denominator, whole=0){
    return parseFloat(whole) + (parseFloat(numerator) / parseFloat(denominator))
  }

  const deduct_instruction_parts = (instructions, active_instruction, relativePos) => {
    //  active_part: int
    //  active_part: str
    //  digested_body: []
    //  Returns object [active_part, active_part, digested_body]
    
    const nothing_trgt = 0
    const drug_trgt = 1
    const verb_trgt = 2
    const drug_unit_trgt = 3
    const peropdicity_unit_trgt = 4
    const admin_route_trgt = 5
    const duration_unit_trgt  = 6
    const conjunction_trgt = 7
    const preposition_trgt = 8
    const indication_of_use_trgt = 9
    
    //  No instructions.
    if (!instructions.length || !active_instruction.length) {return [];}
    /*  Depending on active part suggestions dictionary is updated. */
    const active_text = instructions[active_instruction[0] - 1]
    //  Text before hyphen should be the drug.
    const drug_part = active_text.split('-');
    if(drug_part.length <= 1 || relativePos < active_text.indexOf('-')){
      //  No hyphen probably means there's drug is being redacted.
      return [drug_trgt, drug_part[0], drug_part[0]];
    }
    if(drug_part.length > 2){
      //  More than one hyphen.
      set_part_active(nothing_trgt)
      return [nothing_trgt, drug_part[0], drug_part[0]];
    }

    //  Retrieve instruction body.
    const instruction_raw_body = drug_part[1];
    //  Only one hyphen means drug has been redacted.
    const hyphen_position = active_text.indexOf('-');
    //  Retrieve cursor position relative to instruction body.
    const instruction_body_rel_position = relativePos - (hyphen_position + 1)
    //  Retrieve text before cursor.
    const text_before_cursor = instruction_raw_body.slice(0, instruction_body_rel_position)
    //  Indication of use starts after semicolon if it exist avoid further processing.
    if (text_before_cursor.indexOf(';')>0){
      //  Get text after semicolon.
      const indication_of_use = instruction_raw_body.split(';')[1]
      set_part_active(indication_of_use_trgt)
      return [indication_of_use_trgt, indication_of_use, indication_of_use];
    }
    //  Regex single word or blank.
    const single_word_regex = /^ ?\p{L}+$/u;
    if (single_word_regex.test(text_before_cursor) || !text_before_cursor.trim().length){
      //  No word most probably means action.
      set_part_active(verb_trgt)
      return [verb_trgt, drug_part[1], drug_part[1]];
    }
    //  Process body.
    const number_count_digested_body = digest_instruction(instruction_raw_body)

    /* 
    //  No multiline instructions will be analized.
    if (active_text.indexOf('\n' < 0)){
      console.log('multiline', active_text, active_text.indexOf('\n' < 0))
      return [instruction_part_name[0], active_text, number_count_digested_body];
    }
    */

    //  Active word.
    const active_word = findArrayPosition(number_count_digested_body[2], number_count_digested_body[1], instruction_body_rel_position)
    //  If previous string is number thus a unit must be being redacted.
    if (typeof number_count_digested_body[1][active_word[0] - 1] === 'number'){
      //  Depending on accumulated number count which unit is needed is determined.
      switch (active_word[1]){
        case 1:
          //  Redacting unit of drug.
          set_part_active(drug_unit_trgt)
          return [drug_unit_trgt, number_count_digested_body[2][active_word[0]], number_count_digested_body[1]];
        case 2:
          //  Redacting unit of periodicity.
          set_part_active(peropdicity_unit_trgt)
          return [peropdicity_unit_trgt, number_count_digested_body[2][active_word[0]], number_count_digested_body[1]];
        case 3:
          //  Redacting unit of  duration.
          set_part_active(duration_unit_trgt)
          return [duration_unit_trgt, number_count_digested_body[2][active_word[0]], number_count_digested_body[1]];
        default:
          break;
      }
    }
    //  If not redacting action, numbers or units then conjunctions or prepositions are likely scenarios.
    if (typeof number_count_digested_body[1][active_word[0]] === 'string'){
      //  Depending on accumulated number count either a conjunction, administration route or preposition is needed.
      switch (active_word[1]){
        case 1:
          //  Redacting preposition.
          set_part_active(preposition_trgt)
          return [preposition_trgt, number_count_digested_body[2][active_word[0]], number_count_digested_body[1]];
        case 2:
          //  Search for number followed by str or str followed by str.
          if (typeof(number_count_digested_body[1][active_word[0] - 2])==='number' &&
          typeof(number_count_digested_body[1][active_word[0] - 1])==='string') {
            //  Redacting administration route.
            set_part_active(admin_route_trgt)
            return [admin_route_trgt, number_count_digested_body[2][active_word[0]], number_count_digested_body[1]];
          } else {
            //  Redacting conjunction.
            set_part_active(conjunction_trgt)
            return [conjunction_trgt, number_count_digested_body[2][active_word[0]], number_count_digested_body[1]];
          }
        default:
          console.log('active_word[1]', active_word[1])
          break;
      }
    }
    set_part_active(nothing_trgt)
    return [nothing_trgt, number_count_digested_body[2][active_word[0]], number_count_digested_body[1]];
  }

  const findArrayPosition = (my_array, my_numbers_arr, my_string_position) => {
    let cumulativeLength = 0;
    const len = my_array.length;
    //  Contains number regex.
    let numbers_past = 0;
    for (let i = 0; i < my_array.length; i++) {
      //  Keep track of numbers past positon.
      if (typeof my_numbers_arr[i] === 'number'){
        numbers_past += 1;
      }
      //  Add length of the current count
      cumulativeLength += my_array[i].length;
      // If the cursor position is within the current word, return the index
      if (my_string_position < cumulativeLength) {
          return [i, numbers_past];
      }
      //  Add 1 for the missing space after each word (exception last word)
      cumulativeLength += 1;
      //  If the cursor position is exactly on the space after the word, return the index
      if (my_string_position === cumulativeLength - 1) {
        return [i, numbers_past];
      }
      //  Return last word considering
    }
    // If no exact match, return last.
    return [len - 1, numbers_past];
};

  function digest_instruction (instruction_raw_body){
    //  Split word for processing.
    let raw_body_splitted = instruction_raw_body.split(' ')
    //  Regex for words (u Unicode property escapes).
    const word_regex = /\p{L}+.?/u;
    //  Regex for integers.
    const integer_regex = /^\d+$/;
    //  Regex for fractional numbers (e.g. 1/2).
    const fraction_regex = /\d+\/\d+/;
    //  Regex for fractional numbers (e.g., 1 1/2).
    const whole_fractionRegex = /(\d+)\s+(\d+)\/(\d+)/;
    //  Regex for decimal numbers (e.g., 1.5).
    const decimalRegex = /\d+\.\d+/;
    
    //  To save processed text.
    let digested_body = [];
    let separated_body = [];
    //  Count of contained numbers
    let number_count = 0;

    // Detect decimal, fractionl and whole fractional numbers 1 1/2
    raw_body_splitted.forEach((part, index) => {
      //  Words are just passed.
      if (word_regex.test(part)){
        digested_body.push(part);
        separated_body.push(part);
        return;
      };
      //  Lone fraction number.
      if (fraction_regex.test(part)){
        if (integer_regex.test(raw_body_splitted[index + -1])){
          //  Avoid duplicating fractional numbers.
          return;
        }
        const sliced_fraction =  part.slice('/')
        digested_body.push(fraction_to_float(sliced_fraction[0], sliced_fraction[2]));
        separated_body.push(part);
        number_count += 1;
        return;
      }

      //  Decimal number.
      if (decimalRegex.test(part)){
        digested_body.push(parseFloat(part));
        separated_body.push(part);
        number_count += 1;
        return;
      }

      //  Whole fractional number.
      //  Look for fractions, an extra position and see if it is fractional.
      if (integer_regex.test(part) && index !== instruction_raw_body.length - 1 && fraction_regex.test(raw_body_splitted[index + 1])){
        //  Make one string out of it.
        const new_whole_fractional_number_str = [part, raw_body_splitted[index + 1]].join(' ');
        //  Create float
        const frational_part = raw_body_splitted[index + 1].slice('/');
        //  Whole + fraction = Integer + Numerator[0] Denominator[1].
        const new_whole_fractional_number_float = fraction_to_float(frational_part[0], frational_part[2], part);
        digested_body.push(new_whole_fractional_number_float);
        separated_body.push(`${part} ${frational_part[0]}/${frational_part[2]}`);
        number_count += 1;
        return;
      }

      //  Integer number.
      //  After whole + fraction skip to avoid confusing int with whole + fraction.
      if (integer_regex.test(part)){
        digested_body.push(parseFloat(part))
        separated_body.push(part);
        number_count += 1;
      }
    });
    return [number_count, digested_body, separated_body]
  }

  const updateCursorInfo = () => {
    const range = quillRef.current.getSelection();
    if (range) {
      const position = range.index;
      cursorPosition.current = position;

      const [line] = quillRef.current.getLine(position);
      const lineIndex = quillRef.current.getIndex(line);
      const lineNum = quillRef.current.getLines(0, lineIndex).length;
      setLineNumber(lineNum);

      const relativePos = position - lineIndex;
      setRelativePosition(relativePos);

      const fullText = quillRef.current.getText();
      //  Send the text content to the parent via onTextChange callback
      if (onTextChange) {
        onTextChange(fullText);
      }

      if (fullText.trim()) {
        let delimiters = identifyInstructionsBlocks(fullText);
        if (!delimiters.length) {
          // console.log('No instructions are being redacted.')
          return;
        }
        //console.log('Delimiters.', delimiters)
        const active_instruction_delimiters = identifyActiveInstruction(delimiters, position);
        //console.log('active_instruction_delimiters', active_instruction_delimiters);
        const separated_instructions = separate_instructions(fullText, delimiters);
        //console.log('separated_instructions', separated_instructions)
        //console.log('active_instruction', separated_instructions[active_instruction_delimiters[0] - 1]);
        const active_instruction_part = deduct_instruction_parts(separated_instructions, active_instruction_delimiters, relativePos);
        //console.log('active instruction:', active_instruction_part);
        //setQuery(active_instruction_part[1]);
        //setTarget(active_instruction_part[0]);
        const [targetPart, queryWord] = active_instruction_part;
        set_current_word(queryWord);

        //  Handle DRUG (API) Search: targetPart === 1
        if (active_instruction_part[0] === 1){
          //  Clear local vocabulary target
          setQueryTarget(['', 0]);
          // Set drug query
          setQuery(queryWord);
          // Set active part
          set_part_active(targetPart);
        }
        //  Handle LOCAL Vocabulary Search: targetPart > 1 
        else if (targetPart > 1) {
          // Clear API query
          setQuery('');
          setActiveIndex(null); 
          // Set local vocabulary target
          setQueryTarget([queryWord, targetPart]);
          // Set active part
          set_part_active(targetPart);
        }
        // 3. Handle NO Suggestion (targetPart === 0)
        else {
          //  No text entered yet.
          // Clear both lists
          setQuery('');
          setQueryTarget(['', 0]); 
          setActiveIndex(null);
          setLocalactiveIndex(null);

          set_part_active(targetPart);
        }
        } else{

        }
      

    } else {
      cursorPosition.current = null;
      setLineNumber(null);
      setRelativePosition(null);
    }
  };

  useEffect(() => {
    // Initialize Quill editor
    quillRef.current = new Quill('#editor', {
      theme: 'snow',
      modules: {
        toolbar: false,
        keyboard: {
          bindings: bindings
        }
      },
      placeholder: placeholder
    });
    // Listen for text changes and update cursor info
    quillRef.current.on('text-change', updateCursorInfo);
    // Listen for selection changes and update cursor info
    quillRef.current.on('selection-change', updateCursorInfo);
    //  Arrow down.
    quillRef.current.keyboard.addBinding({ key: 40 }, handleArrowDown);
    //  Arrow up.
    quillRef.current.keyboard.addBinding({ key: 38 }, handleArrowUp);

  }, []);

  return (
    <div>
      <div id="editor" style={{ height: '300px' }}></div>
      {query && data && data.length === 0 && noItemMessage()}
      {error2 && errorMessage()}
      {(() => { 
        switch(part_active) {
          case 0:
            break;
          case 1:
            return listBox(data || [], activeIndex)
          default:
            return <ListBoxLocal items={localData} activeIndex={localactiveIndex} onItemClick={handleItemClick}/>
          }
        })()}
    </div>
  );
};

export default PrescriptionTextBox;